"use client";

import React from "react";
import { Phone, MessageSquare, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DirectContact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  const prefilledMessage = encodeURIComponent("Hallo Hurghada Transfer Hub, ich möchte mich nach einer privaten Transferbuchung erkundigen.");
  const whatsappUrl = `https://wa.me/201010634627?text=${prefilledMessage}`;
  return <section id="talk-to-us-directly" data-nav="light" ref={ref} className="bg-secondary text-secondary-foreground relative border-t border-border py-16 md:py-20 lg:py-24">
      <div className="mx-auto max-w-[1136px] px-6">
        {/* Signature Hairline Rule with Turquoise Terminal Dot */}
        <div className="mb-10 flex items-center gap-2" aria-hidden="true">
          <div className="h-px w-12 bg-border" />
          <div className="size-1.5 rounded-full bg-primary" />
          <div className="h-px w-24 bg-border/60" />
        </div>

        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-14">
          {/* Columns 1-6: Contact details and invitation */}
          <div className={`transition-all duration-700 ease-out lg:col-span-6 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
            <p className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Direkter Kontakt
            </p>
            <h2 className="font-default mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Sprechen Sie direkt mit uns
            </h2>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed">
              Möchten Sie einen Transfer anfragen oder haben Sie Fragen zu Ihrer Fahrt? Unser Team ist rund um die Uhr per WhatsApp oder Telefon erreichbar.
            </p>

            <div className="mt-8 space-y-6">
              {/* Phone & WhatsApp */}
              <div className="flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-primary/25 bg-primary/10 text-primary">
                  <Phone className="size-5" />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase">
                    Telefon &amp; WhatsApp
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-3">
                    <a href="tel:+201010634627" className="font-default text-lg font-semibold text-foreground transition-colors hover:text-primary">
                      +20 10 10634627
                    </a>
                    <span className="inline-flex items-center rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-accent-foreground">
                      24/7 verfügbar
                    </span>
                  </div>
                </div>
              </div>

              {/* Office Location */}
              <div className="flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-primary/25 bg-primary/10 text-primary">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase">
                    Bürostandort
                  </p>
                  <p className="mt-1 text-base font-medium text-foreground">
                    Hurghada, Gouvernement Rotes Meer, Ägypten
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Hurghada, Ägypten
                  </p>
                </div>
              </div>

              {/* 24/7 Support Note */}
              <div className="flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-primary/25 bg-primary/10 text-primary">
                  <Clock className="size-5" />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase">
                    Betriebszeiten
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground leading-normal">
                    Unser Kundenservice und die Flughafen-Disposition sind 24 Stunden am Tag, 7 Tage die Woche aktiv.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Columns 7-12: Action Card / WhatsApp Box */}
          <div className={`transition-all delay-100 duration-700 ease-out lg:col-span-6 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
            <Card className="border-border bg-card p-2 text-card-foreground shadow-sm">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center justify-between border-b border-border pb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <MessageSquare className="size-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        Direkter Kontakt zu unserem Team
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Wir antworten Ihnen persönlich per WhatsApp.
                      </p>
                    </div>
                  </div>
                  <span className="flex items-center gap-1.5 text-xs font-medium text-accent-foreground">
                    <span className="inline-block size-2 rounded-full bg-primary" />
                    Online
                  </span>
                </div>

                <div className="mt-6">
                  <p className="text-xs font-medium text-muted-foreground uppercase">
                    Vorausgefüllte Nachricht versandbereit:
                  </p>
                  <div className="mt-2 rounded-md border border-border/80 bg-muted/60 p-4 text-sm text-foreground italic">
                    &ldquo;Hallo Hurghada Transfer Hub, ich möchte mich nach einer privaten Transferbuchung erkundigen.&rdquo;
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <Button asChild size="lg" className="w-full bg-[#27BA2A] font-medium text-white hover:bg-[#22a025]">
                    <Link to={whatsappUrl} newTab>
                      WhatsApp mit Buchungsanfrage öffnen
                      <ArrowUpRight className="ml-2 size-4" />
                    </Link>
                  </Button>

                  <p className="text-center text-xs text-muted-foreground">
                    Direkter Kontakt: +20 10 10634627 &bull; Hurghada, Ägypten
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}